import { Link } from "react-router-dom";

const CONTACT = "contacto@dosoft.cl";
const UPDATED = "Última actualización: junio de 2026";

function Layout({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-[#F8F9FA] flets-grain" data-testid={testid}>
      <header className="bg-[#0F1115] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src="/frets-logo.png" alt="Frets Transporte" className="h-8 w-auto brightness-0 invert" />
          </Link>
          <nav className="flex items-center gap-4 text-sm text-zinc-300">
            <Link to="/legal/privacidad" className="hover:text-white">Privacidad</Link>
            <Link to="/legal/terminos" className="hover:text-white">Términos</Link>
            <Link to="/legal/cookies" className="hover:text-white">Cookies</Link>
          </nav>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F1115]">{title}</h1>
        {subtitle && <p className="mt-3 text-base text-zinc-600">{subtitle}</p>}
        <p className="mt-2 text-sm text-zinc-400">{UPDATED}</p>
        <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-zinc-700">{children}</div>
        <div className="mt-12 border-t border-zinc-200 pt-6 text-sm text-zinc-500">
          Responsable del tratamiento de datos: <strong>DoSoft</strong>. Ejercicio de derechos y consultas:{" "}
          <a href={`mailto:${CONTACT}`} className="text-[#FF5A36] font-semibold hover:underline">{CONTACT}</a>.
        </div>
        <div className="mt-8">
          <Link to="/" className="text-sm font-semibold text-[#FF5A36] hover:underline">← Volver al inicio</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ h, children }) {
  return (
    <section>
      <h2 className="font-display text-lg font-bold text-[#0F1115] mb-2">{h}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

const Email = () => <a href={`mailto:${CONTACT}`} className="text-[#FF5A36] font-semibold hover:underline">{CONTACT}</a>;

export function PrivacyPolicy() {
  return (
    <Layout title="Política de Privacidad" testid="privacy-page"
      subtitle="Cómo Frets (operado por DoSoft) recopila, usa y protege tus datos personales, conforme a la Ley N° 21.719 sobre Protección de Datos Personales de Chile.">
      <Section h="1. Quiénes somos (responsable del tratamiento)">
        <p>Frets es un marketplace de transporte de carga en Chile operado por <strong>DoSoft</strong>, responsable del tratamiento de tus datos personales. Para cualquier consulta o para ejercer tus derechos, escríbenos a <Email />.</p>
      </Section>
      <Section h="2. Qué datos recopilamos">
        <List items={[
          "Datos de identificación: nombre, apellido, RUT, correo electrónico y teléfono.",
          "Datos de tu empresa (si aplica): razón social, RUT, dirección, giro y usuarios internos.",
          "Datos de ubicación: regiones y comunas de origen/destino y geolocalización del vehículo únicamente durante un traslado activo.",
          "Documentos de acreditación (transportistas): licencias de conducir, permisos, documentación del vehículo y declaración de salud.",
          "Datos operacionales: fletes publicados, ofertas, servicios, evidencias, comprobantes de entrega (POD) y calificaciones.",
          "Datos técnicos mínimos: registros de acceso y cookies estrictamente necesarias para la sesión y la seguridad.",
        ]} />
      </Section>
      <Section h="3. Qué hacemos con tus datos">
        <List items={[
          "Operar el marketplace: publicar y adjudicar fletes, coordinar y ejecutar traslados.",
          "Acreditar transportistas y verificar el cumplimiento de la documentación exigida.",
          "Permitir el rastreo público de la carga mediante el número de traslado.",
          "Gestionar el sistema de calificaciones y reputación bilateral.",
          "Comunicarnos contigo sobre tu cuenta, tus servicios y soporte.",
          "Cumplir obligaciones legales, contables y de seguridad.",
        ]} />
      </Section>
      <Section h="4. Qué NO hacemos con tus datos">
        <List items={[
          "No vendemos tus datos personales a terceros.",
          "No los usamos para publicidad de terceros ni los cedemos con fines comerciales ajenos al servicio.",
          "No solicitamos ni almacenamos datos de tarjetas de crédito en nuestros servidores.",
          "No recopilamos datos sensibles innecesarios; aplicamos minimización de datos.",
          "No compartimos tu información salvo lo necesario para prestar el servicio o por mandato legal.",
        ]} />
      </Section>
      <Section h="5. Base de licitud del tratamiento">
        <p>Tratamos tus datos sobre la base de: (i) la <strong>ejecución del contrato</strong> de uso de la plataforma y de coordinación del transporte; (ii) tu <strong>consentimiento</strong>, otorgado al registrarte y al aceptar cookies no esenciales; (iii) el <strong>cumplimiento de obligaciones legales</strong>; y (iv) el <strong>interés legítimo</strong> en operar y mejorar el servicio, conforme a la Ley N° 21.719.</p>
      </Section>
      <Section h="6. Tus derechos (ARCOP y más)">
        <p>Conforme a la Ley N° 21.719 puedes ejercer, de forma gratuita, los siguientes derechos:</p>
        <List items={[
          "Acceso: conocer qué datos tratamos y obtener una copia.",
          "Rectificación: corregir datos inexactos o desactualizados.",
          "Cancelación / Supresión: solicitar la eliminación de tus datos cuando proceda.",
          "Oposición: oponerte a determinados tratamientos.",
          "Portabilidad: recibir tus datos en un formato estructurado y de uso común.",
          "Bloqueo: suspender temporalmente el tratamiento mientras se resuelve una solicitud.",
        ]} />
        <p>Para ejercerlos escríbenos a <Email />. Responderemos dentro de los plazos legales. Si no estás conforme, puedes reclamar ante la <strong>Agencia de Protección de Datos Personales</strong>.</p>
      </Section>
      <Section h="7. Conservación de datos">
        <p>Conservamos tus datos mientras mantengas una cuenta activa y, posteriormente, sólo durante los plazos exigidos por la ley (por ejemplo, obligaciones tributarias o de responsabilidad). Cumplidos esos plazos, los datos se eliminan o anonimizan.</p>
      </Section>
      <Section h="8. Seguridad de la información">
        <List items={[
          "Control de acceso a la base de datos mediante Row Level Security (RLS).",
          "Cifrado de la información en tránsito (HTTPS/TLS).",
          "Autenticación mediante contraseñas cifradas y tokens de sesión.",
          "Principios de minimización de datos y acceso según necesidad.",
        ]} />
      </Section>
      <Section h="9. Encargados y transferencias">
        <p>Usamos proveedores de infraestructura (bases de datos y servicios de mapas) que actúan como encargados de tratamiento, bajo obligaciones de confidencialidad y seguridad. No realizamos transferencias internacionales de datos con fines comerciales.</p>
      </Section>
      <Section h="10. Menores de edad">
        <p>La plataforma está dirigida a personas mayores de 18 años. No recopilamos intencionadamente datos de menores de edad.</p>
      </Section>
      <Section h="11. Cambios a esta política">
        <p>Podemos actualizar esta política para reflejar cambios legales u operativos. Publicaremos la versión vigente en esta página con su fecha de actualización.</p>
      </Section>
    </Layout>
  );
}

export function TermsConditions() {
  return (
    <Layout title="Términos y Condiciones" testid="terms-page"
      subtitle="Condiciones que regulan el uso de la plataforma Frets, operada por DoSoft.">
      <Section h="1. Objeto y aceptación">
        <p>Frets es un marketplace que conecta a clientes que necesitan transportar carga con transportistas que ofrecen sus servicios en Chile. Al registrarte y usar la plataforma aceptas estos Términos y la Política de Privacidad. DoSoft facilita la conexión, pero <strong>no es parte del contrato de transporte</strong> celebrado entre cliente y transportista.</p>
      </Section>
      <Section h="2. Qué ofrece Frets">
        <List items={[
          "Publicación de fletes con precio referencial y recepción de ofertas o contraofertas.",
          "Adjudicación de servicios y generación de un número de traslado único.",
          "Herramientas de coordinación, rastreo, evidencia y comprobante de entrega (POD).",
          "Sistema de calificación bilateral entre clientes y transportistas.",
          "Emparejamiento inteligente de oportunidades según zona, vehículo y servicio.",
        ]} />
      </Section>
      <Section h="3. Qué NO es Frets">
        <List items={[
          "No es una empresa de transporte ni presta directamente el servicio de flete.",
          "No garantiza la contratación, disponibilidad ni el resultado de un traslado.",
          "No custodia la carga ni asume la responsabilidad del transportista sobre ella.",
          "No interviene en los precios: son acordados libremente entre las partes.",
        ]} />
      </Section>
      <Section h="4. Cuentas y veracidad">
        <p>Debes entregar información veraz y mantener la confidencialidad de tus credenciales. Los transportistas deben mantener vigente su documentación de acreditación (licencias, permisos y documentos del vehículo). El uso de subusuarios queda bajo responsabilidad del titular de la cuenta.</p>
      </Section>
      <Section h="5. Obligaciones de las partes">
        <List items={[
          "Cliente: declarar correctamente la carga, su naturaleza, peso y condiciones.",
          "Transportista: ejecutar el traslado con diligencia y cuidar la carga.",
          "Ambos: actuar de buena fe, respetar los acuerdos y usar las calificaciones con honestidad.",
        ]} />
      </Section>
      <Section h="6. Conducta prohibida">
        <List items={[
          "Usar la plataforma para fines ilícitos o transportar mercancías prohibidas.",
          "Manipular calificaciones, ofertas o el sistema de rastreo.",
          "Suplantar identidades o entregar información falsa.",
          "Intentar vulnerar la seguridad o el acceso a datos de terceros.",
        ]} />
      </Section>
      <Section h="7. Responsabilidad">
        <p>La responsabilidad por la correcta ejecución del transporte recae en el transportista, y la veracidad de la carga en el cliente. DoSoft provee la plataforma "tal cual" y no responde por daños derivados del acuerdo entre las partes, salvo en lo que la ley disponga de forma imperativa.</p>
      </Section>
      <Section h="8. Suspensión de cuentas">
        <p>Podemos suspender o cancelar cuentas que incumplan estos Términos, que presenten documentación vencida o que afecten la seguridad o el buen funcionamiento de la comunidad.</p>
      </Section>
      <Section h="9. Legislación y contacto">
        <p>Estos Términos se rigen por las leyes de la República de Chile. Para consultas escríbenos a <Email />.</p>
      </Section>
    </Layout>
  );
}

export function CookiesPolicy() {
  return (
    <Layout title="Política de Cookies" testid="cookies-page"
      subtitle="Cómo usamos cookies y tecnologías de almacenamiento en Frets.">
      <Section h="1. Qué son las cookies">
        <p>Las cookies y el almacenamiento local son pequeños archivos que el sitio guarda en tu dispositivo para funcionar correctamente y recordar tus preferencias.</p>
      </Section>
      <Section h="2. Qué cookies usamos">
        <List items={[
          "Estrictamente necesarias: mantienen tu sesión iniciada y protegen la seguridad. Sin ellas la plataforma no funciona.",
          "De preferencias: recuerdan tu decisión sobre este aviso de cookies (aceptar o rechazar).",
        ]} />
      </Section>
      <Section h="3. Qué NO hacemos con cookies">
        <List items={[
          "No usamos cookies de publicidad de terceros.",
          "No hacemos seguimiento entre sitios (cross-site tracking).",
          "No vendemos ni cedemos tu actividad de navegación.",
        ]} />
      </Section>
      <Section h="4. Gestión del consentimiento">
        <p>Al ingresar te mostramos un aviso donde puedes <strong>aceptar</strong> o <strong>rechazar</strong> las cookies no esenciales; tu elección se guarda en tu navegador. Las cookies estrictamente necesarias no requieren consentimiento por ser indispensables, conforme a la Ley N° 21.719. Puedes borrar las cookies desde la configuración de tu navegador en cualquier momento.</p>
      </Section>
      <Section h="5. Contacto">
        <p>Para dudas sobre esta política, escríbenos a <Email />.</p>
      </Section>
    </Layout>
  );
}
